import { Move, RotateCw } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
    reasonWhileDisabled,
    refused,
    type ToolEntry,
} from '~/components/toolbar/shared/toolEntry';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import {
    pickedToolState,
    registrationModeState,
} from '~/state/atoms/toolsAtoms';

/** The registration tool's two drag modes. */
export interface RegistrationModeEntries {
    translation: ToolEntry;
    rotation: ToolEntry;
}

/**
 * The registration tool's translation and rotation modes as
 * {@link ToolEntry} rows. Picking one arms the registration tool in that
 * mode.
 */
export function useRegistrationModeEntries(
    isDisabled: boolean,
    disabledReason: string | undefined
): RegistrationModeEntries {
    const intl = useIntl();
    const [tool, pickTool] = useRecoilState(pickedToolState);
    const [registrationMode, setRegistrationMode] = useRecoilState(
        registrationModeState
    );

    const entry = (
        id: string,
        mode: RegistrationMode,
        labelId: string,
        icon: ToolEntry['icon'],
        hotkey: string
    ): ToolEntry => {
        const fields = {
            label: intl.formatMessage({ id: labelId }),
            hotkey,
            disabled: isDisabled,
            disabledReason: reasonWhileDisabled(
                intl,
                isDisabled,
                disabledReason
            ),
        };
        const arm = () => {
            if (!refused(fields)) {
                setRegistrationMode((prev) => ({ ...prev, mode }));
                pickTool('registration');
            }
        };
        return {
            ...fields,
            id,
            icon,
            selected: tool === 'registration' && registrationMode.mode === mode,
            arm,
            toggle: arm,
        };
    };

    return {
        translation: entry(
            'registration-translate',
            RegistrationMode.TRANSLATION,
            'translation',
            <Move />,
            't'
        ),
        // R cycles the row planes; Y sits beside T.
        rotation: entry(
            'registration-rotate',
            RegistrationMode.ROTATION,
            'rotation',
            <RotateCw />,
            'y'
        ),
    };
}
